import { useCallback, useEffect, useState } from "react";
import {
  Activity, AlertTriangle, Archive, BadgeCheck, Banknote, Bell, Blocks, Bot, Boxes, Brain,
  Building2, ChevronDown, Clock, Coins, Cpu, CreditCard, Database, FileText, FlaskConical,
  Gauge, Globe, HardDrive, KeyRound, LayoutDashboard, LifeBuoy, LineChart, ListChecks, Lock,
  Plug, Receipt, RotateCcw, ScrollText, ShieldCheck, Siren, SlidersHorizontal, Tags,
  ToggleLeft, UserCog, UserSearch, Users, Webhook, Workflow,
} from "lucide-react";
import { useAdmin } from "../lib/useAdmin";

/* Structure de la navigation. Chaque entrée porte la permission exigée, reprise
   du catalogue de docs/rbac.md quand elle y figure (users.read, sessions.read,
   audit.read, flags.read, incidents.read, gdpr.read, rbac.read, system.read…).
   Les sections sans contrepartie documentée gardent un nom de domaine explicite
   (orgs.read, webhooks.read…) : faute d'exister côté serveur, elles restent
   masquées, ce qui est le comportement voulu.
   sans elle, l'entrée n'est pas rendue du tout — jamais grisée, pour ne pas
   révéler l'existence d'une section à qui n'y a pas droit.

   `future: true` marque les sections dont l'objet n'existe pas encore dans
   Perform (organisations, facturation, IA, SCIM, webhooks, files de jobs).
   Ces pages affichent l'état NotApplicable plutôt que des chiffres inventés. */
export const ADMIN_NAV = [
  {
    id: "overview",
    label: "Vue d'ensemble",
    items: [
      { key: "dashboard", label: "Tableau de bord", to: "/admin", icon: LayoutDashboard, perm: "settings.read" },
      { key: "analytics", label: "Analytique", to: "/admin/analytique", icon: LineChart, perm: "analytics.read", future: true },
      { key: "system", label: "Système", to: "/admin/systeme", icon: Cpu, perm: "settings.read" },
    ],
  },
  {
    id: "customers",
    label: "Clients",
    items: [
      { key: "orgs", label: "Organisations", to: "/admin/organisations", icon: Building2, perm: "organizations.read", future: true },
      { key: "users", label: "Utilisateurs", to: "/admin/utilisateurs", icon: Users, perm: "users.read" },
      { key: "domains", label: "Domaines", to: "/admin/domaines", icon: Globe, perm: "organizations.read", future: true },
    ],
  },
  {
    id: "billing",
    label: "Facturation",
    items: [
      { key: "subscriptions", label: "Abonnements", to: "/admin/abonnements", icon: BadgeCheck, perm: "billing.read", future: true },
      { key: "payments", label: "Paiements", to: "/admin/paiements", icon: CreditCard, perm: "billing.read", future: true },
      { key: "invoices", label: "Factures", to: "/admin/factures", icon: Receipt, perm: "billing.read", future: true },
      { key: "refunds", label: "Remboursements", to: "/admin/remboursements", icon: Banknote, perm: "billing.read", future: true },
      { key: "plans", label: "Offres", to: "/admin/offres", icon: Tags, perm: "billing.read", future: true },
    ],
  },
  {
    id: "product",
    label: "Produit",
    items: [
      { key: "features", label: "Fonctionnalités", to: "/admin/fonctionnalites", icon: Blocks, perm: "feature_flags.read" },
      { key: "flags", label: "Feature flags", to: "/admin/flags", icon: ToggleLeft, perm: "feature_flags.read" },
      { key: "experiments", label: "Expérimentations", to: "/admin/experimentations", icon: FlaskConical, perm: "feature_flags.read", future: true },
      { key: "releases", label: "Versions", to: "/admin/versions", icon: Boxes, perm: "settings.read" },
    ],
  },
  {
    id: "security",
    label: "Sécurité",
    items: [
      { key: "security", label: "Centre de sécurité", to: "/admin/securite", icon: ShieldCheck, perm: "security.read" },
      { key: "sessions", label: "Sessions", to: "/admin/sessions", icon: Clock, perm: "security.read" },
      { key: "sso", label: "SSO", to: "/admin/sso", icon: Lock, perm: "api.read", future: true },
      { key: "scim", label: "SCIM", to: "/admin/scim", icon: UserCog, perm: "api.read", future: true },
      { key: "api-security", label: "Sécurité de l'API", to: "/admin/securite-api", icon: KeyRound, perm: "settings.read" },
      { key: "audit", label: "Journal d'audit", to: "/admin/audit", icon: ScrollText, perm: "audit.read" },
    ],
  },
  {
    id: "support",
    label: "Support",
    items: [
      { key: "tickets", label: "Tickets", to: "/admin/tickets", icon: LifeBuoy, perm: "settings.read" },
      { key: "impersonation", label: "Impersonation", to: "/admin/impersonation", icon: UserSearch, perm: "users.impersonate" },
      { key: "customer-activity", label: "Activité client", to: "/admin/activite-client", icon: Activity, perm: "users.read" },
    ],
  },
  {
    id: "developers",
    label: "Développeurs",
    items: [
      { key: "api", label: "API", to: "/admin/api", icon: Workflow, perm: "settings.read" },
      { key: "api-keys", label: "Clés d'API", to: "/admin/cles-api", icon: KeyRound, perm: "settings.read" },
      { key: "oauth", label: "OAuth", to: "/admin/oauth", icon: Plug, perm: "api.read", future: true },
      { key: "webhooks", label: "Webhooks", to: "/admin/webhooks", icon: Webhook, perm: "api.read", future: true },
      { key: "integrations", label: "Intégrations", to: "/admin/integrations", icon: Blocks, perm: "settings.read" },
    ],
  },
  {
    id: "ai",
    label: "IA",
    items: [
      { key: "ai-usage", label: "Usage", to: "/admin/ia/usage", icon: Gauge, perm: "analytics.read", future: true },
      { key: "ai-models", label: "Modèles", to: "/admin/ia/modeles", icon: Brain, perm: "analytics.read", future: true },
      { key: "ai-costs", label: "Coûts", to: "/admin/ia/couts", icon: Coins, perm: "analytics.read", future: true },
      { key: "ai-safety", label: "Sûreté", to: "/admin/ia/surete", icon: Bot, perm: "analytics.read", future: true },
    ],
  },
  {
    id: "data",
    label: "Données",
    items: [
      { key: "storage", label: "Stockage", to: "/admin/stockage", icon: HardDrive, perm: "settings.read" },
      { key: "exports", label: "Exports", to: "/admin/exports", icon: Archive, perm: "data.export" },
      { key: "gdpr", label: "Demandes RGPD", to: "/admin/rgpd", icon: FileText, perm: "data.export" },
      { key: "retention", label: "Rétention", to: "/admin/retention", icon: RotateCcw, perm: "settings.read" },
    ],
  },
  {
    id: "operations",
    label: "Opérations",
    items: [
      { key: "incidents", label: "Incidents", to: "/admin/incidents", icon: Siren, perm: "incidents.read" },
      { key: "status", label: "État", to: "/admin/etat", icon: Activity, perm: "incidents.read" },
      { key: "jobs", label: "Jobs", to: "/admin/jobs", icon: ListChecks, perm: "settings.read", future: true },
      { key: "queues", label: "Files", to: "/admin/files", icon: Database, perm: "settings.read", future: true },
      { key: "errors", label: "Erreurs", to: "/admin/erreurs", icon: AlertTriangle, perm: "settings.read" },
    ],
  },
  {
    id: "settings",
    label: "Réglages",
    items: [
      { key: "admins", label: "Administrateurs", to: "/admin/administrateurs", icon: UserCog, perm: "settings.read" },
      { key: "roles", label: "Rôles", to: "/admin/roles", icon: ShieldCheck, perm: "settings.read" },
      { key: "permissions", label: "Permissions", to: "/admin/permissions", icon: Lock, perm: "settings.read" },
      { key: "general", label: "Général", to: "/admin/reglages", icon: SlidersHorizontal, perm: "settings.read" },
      { key: "settings-security", label: "Sécurité", to: "/admin/reglages/securite", icon: Lock, perm: "settings.read" },
      { key: "notifications", label: "Notifications", to: "/admin/reglages/notifications", icon: Bell, perm: "settings.read" },
    ],
  },
];

// Table plate, pratique pour la palette de commandes et le fil d'Ariane.
export const NAV_INDEX = ADMIN_NAV.flatMap(group => group.items.map(item => ({ ...item, group: group.label })));

const STORE = "perform.admin.nav-collapsed";

function readCollapsed() {
  try {
    const raw = window.localStorage.getItem(STORE);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// L'entrée active est la plus longue destination qui préfixe le chemin courant.
export function activeKey(path, groups = ADMIN_NAV) {
  let best = null;
  groups.forEach(group => group.items.forEach(item => {
    if (path === item.to || path.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }));
  return best?.key ?? null;
}

export default function AdminNav({ groups = ADMIN_NAV, currentPath = "/admin", onNavigate, counts = {}, onItemClick }) {
  const { can } = useAdmin();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORE, JSON.stringify(collapsed));
    } catch {
      /* Sans stockage, les groupes repartent ouverts à chaque visite. */
    }
  }, [collapsed]);

  const toggle = useCallback(id => {
    setCollapsed(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));
  }, []);

  const active = activeKey(currentPath, groups);
  const visibleGroups = groups
    .map(group => ({ ...group, items: group.items.filter(item => can(item.perm)) }))
    .filter(group => group.items.length > 0);

  const go = (event, item) => {
    onItemClick?.(item);
    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onNavigate(item.to, item);
  };

  if (!visibleGroups.length) {
    return (
      <p className="adm-nav-empty" data-testid="nav-empty">
        Aucune section n'est ouverte à votre compte. Demandez un rôle à un administrateur de la console.
      </p>
    );
  }

  return (
    <nav aria-label="Sections de la console" data-testid="admin-nav">
      {visibleGroups.map(group => {
        const isCollapsed = collapsed.includes(group.id);
        return (
          <div className="adm-nav-group" key={group.id}>
            <button
              type="button"
              className="adm-nav-group-head"
              aria-expanded={!isCollapsed}
              aria-controls={`nav-${group.id}`}
              onClick={() => toggle(group.id)}
              data-testid={`nav-group-${group.id}`}
            >
              <ChevronDown size={12} />
              <span>{group.label}</span>
              {isCollapsed && <em>{group.items.length}</em>}
            </button>
            {!isCollapsed && (
              <div className="adm-nav-items" id={`nav-${group.id}`}>
                {group.items.map(item => {
                  const Icon = item.icon;
                  const count = counts[item.key];
                  return (
                    <a
                      key={item.key}
                      href={item.to}
                      className={`adm-nav-item${item.key === active ? " is-active" : ""}`}
                      aria-current={item.key === active ? "page" : undefined}
                      onClick={e => go(e, item)}
                      title={item.future ? `${item.label} — section sans objet pour l'instant` : item.label}
                      data-testid={`nav-${item.key}`}
                    >
                      <Icon size={14} />
                      <span>{item.label}</span>
                      {count !== undefined && <span className="adm-nav-count">{count}</span>}
                      {count === undefined && item.future && <span className="adm-nav-count" aria-label="sans objet pour l'instant">—</span>}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
